import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useConfirm } from '@/data/context/ConfirmModalContext';
import useStudentReport from '@/data/hook/useStudentReport';
import { handleUserFullName } from '@/functions/handleUserFullName';
import Enrollment from '@/models/Enrollment';
import ScheduledLesson from '@/models/ScheduledLesson';
import ScheduledLessonReport, {
  PresenceEnum,
} from '@/models/ScheduledLessonReport';

export type UsePresenceHandlerProps = {
  enrollment: Enrollment;
  report?: ScheduledLessonReport;
  scheduledLesson: ScheduledLesson;
  handleEnrollment?: (enrollment: Enrollment) => void;
};

export default function usePresenceHandler({
  enrollment,
  report,
  scheduledLesson,
  handleEnrollment,
}: UsePresenceHandlerProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'modalDisable',
  });
  const { t: tPresence } = useTranslation('translation', {
    keyPrefix: 'scheduledLessonReportModal',
  });

  const confirmRemoveAttendance = useConfirm();

  const studentId = enrollment.student.id;
  const scheduledLessonId = scheduledLesson.id;

  const { create, update, loadingCreate, loadingUpdate } = useStudentReport({
    scheduledLessonId,
    fetchReports: false,
    successMessage: tPresence('presenceFilled'),
    enabled: !report,
  });

  const removeAttendance = useCallback(
    (id: number) => {
      confirmRemoveAttendance({
        variant: 'destructive',
        title: t('modalStudentAbsence.warning'),
        confirmLabel: t('confirm'),
        description: (
          <span className="flex flex-col gap-2">
            <span className="font-semibold capitalize text-base-content">
              {handleUserFullName(enrollment.student)}
            </span>
            {t('modalStudentAbsence.lostChangesWarning')}
          </span>
        ),
        onConfirm: () =>
          update({
            id,
            changes: { presence: PresenceEnum.MISSED, presenceReward: null },
          }),
      });
    },
    [confirmRemoveAttendance, t, enrollment.student, update],
  );

  const handlePresence = useCallback(
    async (presence: PresenceEnum) => {
      if (report) {
        const id = report.id;
        if (presence === PresenceEnum.MISSED) return removeAttendance(id);

        const setedPresence = presence === PresenceEnum.NONE ? null : presence;
        await update({ id, changes: { presence: setedPresence } });
      } else {
        await create({
          student: studentId,
          scheduledLesson: scheduledLessonId,
          presence,
        });
      }

      handleEnrollment?.(enrollment);
    },
    [
      report,
      removeAttendance,
      update,
      create,
      studentId,
      scheduledLessonId,
      handleEnrollment,
      enrollment,
    ],
  );

  return {
    report,
    loading: loadingCreate || loadingUpdate,
    handlePresence,
  };
}
